import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { createWeb3Modal, defaultConfig } from '@web3modal/ethers/react';
import {
  CONTRACTS,
  NETWORKS,
  MARKET_ABI,
  ERC20_ABI,
  ERC1155_ABI,
  WEB3MODAL_PROJECT_ID
} from '../config/contracts';

// Initialize Web3Modal
const metadata = {
  name: 'APY Prediction Market',
  description: 'Predict APY changes on DeFi protocols',
  url: 'https://drift.markets',
  icons: []
};

const ethersConfig = defaultConfig({ metadata });

createWeb3Modal({
  ethersConfig,
  chains: [NETWORKS.BASE],
  projectId: WEB3MODAL_PROJECT_ID,
  enableAnalytics: false
});

const Web3Context = createContext(null);

export function useWeb3() {
  const context = useContext(Web3Context);
  if (!context) {
    throw new Error('useWeb3 must be used within a Web3Provider');
  }
  return context;
}

export function Web3Provider({ children }) {
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [address, setAddress] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [marketContract, setMarketContract] = useState(null);
  const [positionTokenContract, setPositionTokenContract] = useState(null);
  const [usdcContract, setUsdcContract] = useState(null);
  const [isWrongNetwork, setIsWrongNetwork] = useState(false);

  // Initialize read-only provider
  useEffect(() => {
    const readOnlyProvider = new ethers.JsonRpcProvider(NETWORKS.BASE.rpcUrl);
    setProvider(readOnlyProvider);

    const readOnlyMarket = new ethers.Contract(
      CONTRACTS.MARKET_ADDRESS,
      MARKET_ABI,
      readOnlyProvider
    );
    setMarketContract(readOnlyMarket);

    const readOnlyPositionToken = new ethers.Contract(
      CONTRACTS.POSITION_TOKEN_ADDRESS,
      ERC1155_ABI,
      readOnlyProvider
    );
    setPositionTokenContract(readOnlyPositionToken);
  }, []);

  // Handle wallet connection
  const connectWallet = useCallback(async (walletProvider) => {
    if (!walletProvider) {
      // Disconnected - fall back to read-only
      const readOnlyProvider = new ethers.JsonRpcProvider(NETWORKS.BASE.rpcUrl);
      setProvider(readOnlyProvider);
      setSigner(null);
      setAddress(null);
      setChainId(null);
      setIsWrongNetwork(false);

      const readOnlyMarket = new ethers.Contract(
        CONTRACTS.MARKET_ADDRESS,
        MARKET_ABI,
        readOnlyProvider
      );
      setMarketContract(readOnlyMarket);

      const readOnlyPositionToken = new ethers.Contract(
        CONTRACTS.POSITION_TOKEN_ADDRESS,
        ERC1155_ABI,
        readOnlyProvider
      );
      setPositionTokenContract(readOnlyPositionToken);
      setUsdcContract(null);
      return;
    }

    try {
      const browserProvider = new ethers.BrowserProvider(walletProvider);
      const walletSigner = await browserProvider.getSigner();
      const walletAddress = await walletSigner.getAddress();
      const network = await browserProvider.getNetwork();
      const currentChainId = Number(network.chainId);

      setProvider(browserProvider);
      setSigner(walletSigner);
      setAddress(walletAddress);
      setChainId(currentChainId);
      setIsWrongNetwork(currentChainId !== NETWORKS.BASE.chainId);

      // Create contracts with signer
      const market = new ethers.Contract(
        CONTRACTS.MARKET_ADDRESS,
        MARKET_ABI,
        walletSigner
      );
      setMarketContract(market);

      const positionToken = new ethers.Contract(
        CONTRACTS.POSITION_TOKEN_ADDRESS,
        ERC1155_ABI,
        walletSigner
      );
      setPositionTokenContract(positionToken);

      const usdc = new ethers.Contract(
        CONTRACTS.USDC_ADDRESS,
        ERC20_ABI,
        walletSigner
      );
      setUsdcContract(usdc);

    } catch (error) {
      console.error('Error connecting wallet:', error);
    }
  }, []);

  // Switch to Base network
  const switchToBase = useCallback(async () => {
    if (!window.ethereum) {
      alert('No Web3 wallet detected');
      return;
    }

    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: '0x' + NETWORKS.BASE.chainId.toString(16) }],
      });
    } catch (switchError) {
      // Chain not added - add it
      if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: '0x' + NETWORKS.BASE.chainId.toString(16),
              chainName: NETWORKS.BASE.name,
              nativeCurrency: {
                name: NETWORKS.BASE.currency,
                symbol: NETWORKS.BASE.currency,
                decimals: 18
              },
              rpcUrls: [NETWORKS.BASE.rpcUrl],
              blockExplorerUrls: [NETWORKS.BASE.explorerUrl]
            }],
          });
        } catch (addError) {
          console.error('Error adding chain:', addError);
          alert('Failed to add Base network. Please add it manually.');
        }
      } else {
        console.error('Error switching chain:', switchError);
        alert('Failed to switch network. Please try manually in your wallet.');
      }
    }
  }, []);

  // Listen for chain changes
  useEffect(() => {
    if (window.ethereum) {
      const handleChainChanged = () => {
        window.location.reload();
      };
      window.ethereum.on('chainChanged', handleChainChanged);
      return () => {
        window.ethereum.removeListener('chainChanged', handleChainChanged);
      };
    }
  }, []);

  const value = {
    provider,
    signer,
    address,
    chainId,
    isConnected: !!address,
    isWrongNetwork,
    marketContract,
    positionTokenContract,
    usdcContract,
    connectWallet,
    switchToBase,
  };

  return (
    <Web3Context.Provider value={value}>
      {children}
    </Web3Context.Provider>
  );
}
